import EF from 'ef/ef';
import Backbone from 'backbone';
import $ from 'jquery';
import _ from 'lodash';
import { LocalStorageService } from 'services/data-handler-services/web-storage-service';

const Enrollment = Backbone.Model.extend({
  initialize: function () {
    this.data = new EnrollmentDataCollection([], { enrollment: this });
  },

  url: function () {
    return EF.url(`/progress/${EF.enrollment.get('id')}.json`);
  },

  sync: function (method, model, options) {
    if (model) {
      model.set('data', {
        type: 'progress',
        id: this.get('id'),
        attributes: {
          enrollment_data: EF.enrollment.data.toJSON(),
        },
      });
    }
    return Backbone.Model.prototype.sync.apply(this, [method, model, options]);
  },

  parse: function (response) {
    return EF.ContentProvider.EnrollmentService.parseEnrollment(this, response);
  },

  loadData: function (options) {
    if (EF.options.local) {
      const deferred = $.Deferred();
      if (options && _.isFunction(options.success)) {
        deferred.done(options.success);
      }
      return deferred.resolve().promise();
    } else {
      //return the jqXHR promise so it can be used as an alternative method for
      //defining the callback
      return this.data.fetch(options);
    }
  },

  _localED: function () {
    let ed = LocalStorageService.getItem(`enrollment-data-${this.get('id')}`);
    if (!ed) {
      ed = {};
    }
    return ed;
  },

  _memoryED: {},

  // ### setData
  // sets freeform data at the enrollment level. Takes a key, value and
  // options hash. Options hash may have a 'success' function that will
  // be called then the data is successfully saved.
  //
  setData: function (key, value, options) {
    this._memoryED[key] = value;

    value = EF.JSON.stringify(value);

    if (EF.options.local) {
      this._setDataLocal(key, value, options);
    } else {
      this._setDataLocal(key, value);
      return this._setDataRemote(key, value, options);
    }
  },

  _setDataLocal: function (key, value, options) {
    const ed = this._localED();
    ed[key] = value;
    LocalStorageService.setItem(`enrollment-data-${this.get('id')}`, ed);
    if (options && options.success) {
      options.success(true);
    }
  },

  _setDataRemote: function (key, value, options) {
    const dataObj = this.data.findOrInitByKey(key);
    dataObj.set('data', value);
    EF.ContentProvider.EnrollmentService.saveData(this, options, dataObj);
    return dataObj;
  },

  getAllData: function () {
    if (EF.options.local) {
      return this._localED();
    } else {
      return this.data.reduce((agg, dataValue) => {
        agg[dataValue.key] = dataValue.data;
        return agg;
      }, {});
    }
  },

  // ### getData
  // gets the freeform data stored at the enrollment level by key. Takes a key
  // and an options hash. Options hash should have a callback with a success
  // function defined. The success function gets passed the value stored
  // at that key for the current enrollment
  getData: function (key, options) {
    options = options || { success: function () {} };
    if (EF.options.local) {
      let data = this._getDataLocal(key, options);
      if (data === undefined) {
        data = this._memoryED[key];
        this._setDataLocal(key, data);
      }
      return data;
    } else {
      const dataObj = this.data.findByKey(key);
      let data = this._parseData(dataObj);
      if (data === undefined) {
        data = this._getDataLocal(key, options);
        if (data === undefined) {
          data = this._memoryED[key];
          this._setDataLocal(key, data);
        }
        if (data) {
          this._setDataRemote(key, EF.JSON.stringify(data));
        }
      }
      options.success(data);
      return data;
    }
  },

  _parseData: function (dataObj) {
    let data;
    if (dataObj) {
      data = dataObj.get('data');
      if (typeof data === 'string') {
        try {
          data = EF.JSON.parse(data);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e);
        }
      }
    }
    return data;
  },

  _getDataLocal: function (key, options) {
    const ed = this._localED(),
      str = ed[key];
    const data = EF.JSON.parse(str);

    options.success(data);
    return data;
  },

  isPastIntersession: function () {
    const pastIntStates = [
      'part_2_ready',
      'part_2_started',
      'part_2_complete',
      'tier_2_certified',
      'tier_2_complete',
      'tier_2_started',
    ];
    return pastIntStates.indexOf(this.get('state')) > -1;
  },
});

const EnrollmentData = Backbone.Model.extend({
  idAttribute: 'key',
  initialize: function () {
    this.enrollment = this.collection.enrollment;
  },
  url: function () {
    return EF.url(`/sdk/enrollments/${this.enrollment.get('id')}/enrollment_data/${this.get('key')}.json`);
  },

  parse: function (attrs) {
    if (attrs && attrs.data && typeof attrs.data === 'string') {
      try {
        attrs.data = EF.JSON.parse(attrs.data);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }
    return attrs;
  },
});

const EnrollmentDataCollection = Backbone.Collection.extend({
  model: EnrollmentData,
  initialize: function (models, options) {
    this.enrollment = options.enrollment;
  },

  url: function () {
    return EF.url(`/sdk/enrollments/${this.enrollment.get('id')}/enrollment_data.json`);
  },

  findByKey: function (key) {
    return this.find(function (model) {
      return model.get('key') === key;
    });
  },

  findOrInitByKey: function (key, data) {
    let model = this.findByKey(key);
    if (!model) {
      model = this.add({ key: key, data: data });
    }
    return model;
  },
});

export { Enrollment, EnrollmentData, EnrollmentDataCollection };
